<template>
  <div class="create-customer-form">
    <VeeForm :initial-values="customer" novalidate @submit="handleSubmit" @invalid-submit="invalidSubmit">
      <p v-if="error" ref="errorSummary" class="alert alert-danger" tabindex="-1" role="alert">{{ error }}</p>
      <fieldset :disabled="saving || !!createdCompanyId" class="create-fields">
        <section class="create-card">
          <h2>Company &amp; contact</h2>
          <p>The contact becomes the first user for this customer.</p>
          <div class="create-grid">
            <Field v-slot="{ field, errors }" name="name" label="Company name" :rules="'required'">
              <div class="create-field">
                <label :for="id + '-name'">Company name *</label><input
                  v-bind="field"
                  :id="id + '-name'"
                  v-model="customer.name"
                  type="text"
                  autocomplete="organization"
                  class="form-control"
                  :class="{ 'is-invalid': errors.length }"
                  :aria-invalid="!!errors.length"
                  :aria-describedby="errors.length ? id + '-name-error' : undefined"
                />
                <p v-if="errors.length" :id="id + '-name-error'" class="invalid-feedback d-block">{{ errors[0] }}</p>
              </div>
            </Field><Field v-slot="{ field, errors }" name="clientNumber" label="Client number" :rules="'required'">
              <div class="create-field">
                <label :for="id + '-clientNumber'">Client number *</label><input
                  v-bind="field"
                  :id="id + '-clientNumber'"
                  v-model="customer.clientNumber"
                  type="text"
                  autocomplete="off"
                  class="form-control"
                  :class="{ 'is-invalid': errors.length }"
                  :aria-invalid="!!errors.length"
                  :aria-describedby="errors.length ? id + '-clientNumber-error' : undefined"
                /><small>Use the customer number from your business system.</small>
                <p v-if="errors.length" :id="id + '-clientNumber-error'" class="invalid-feedback d-block">
                  {{ errors[0] }}
                </p>
              </div>
            </Field><Field v-slot="{ field, errors }" name="contactName" label="First name" :rules="'required'">
              <div class="create-field">
                <label :for="id + '-contactName'">First name *</label><input
                  v-bind="field"
                  :id="id + '-contactName'"
                  v-model="customer.contactName"
                  type="text"
                  autocomplete="given-name"
                  class="form-control"
                  :class="{ 'is-invalid': errors.length }"
                  :aria-invalid="!!errors.length"
                  :aria-describedby="errors.length ? id + '-contactName-error' : undefined"
                />
                <p v-if="errors.length" :id="id + '-contactName-error'" class="invalid-feedback d-block">
                  {{ errors[0] }}
                </p>
              </div>
            </Field><Field v-slot="{ field, errors }" name="contactLastName" label="Last name" :rules="'required'">
              <div class="create-field">
                <label :for="id + '-contactLastName'">Last name *</label><input
                  v-bind="field"
                  :id="id + '-contactLastName'"
                  v-model="customer.contactLastName"
                  type="text"
                  autocomplete="family-name"
                  class="form-control"
                  :class="{ 'is-invalid': errors.length }"
                  :aria-invalid="!!errors.length"
                  :aria-describedby="errors.length ? id + '-contactLastName-error' : undefined"
                />
                <p v-if="errors.length" :id="id + '-contactLastName-error'" class="invalid-feedback d-block">
                  {{ errors[0] }}
                </p>
              </div>
            </Field><Field v-slot="{ field, errors }" name="email" label="Email address" :rules="'required|email'">
              <div class="create-field">
                <label :for="id + '-email'">Email address *</label><input
                  v-bind="field"
                  :id="id + '-email'"
                  v-model="customer.email"
                  type="email"
                  autocomplete="email"
                  class="form-control"
                  :class="{ 'is-invalid': errors.length }"
                  :aria-invalid="!!errors.length"
                  :aria-describedby="errors.length ? id + '-email-error' : undefined"
                /><small>Used for the contact’s login.</small>
                <p v-if="errors.length" :id="id + '-email-error'" class="invalid-feedback d-block">{{ errors[0] }}</p>
              </div>
            </Field><Field v-slot="{ field, errors }" name="phoneNumber" label="Phone number" :rules="'required'">
              <div class="create-field">
                <label :for="id + '-phoneNumber'">Phone number *</label><input
                  v-bind="field"
                  :id="id + '-phoneNumber'"
                  v-model="customer.phoneNumber"
                  type="tel"
                  autocomplete="tel"
                  class="form-control"
                  :class="{ 'is-invalid': errors.length }"
                  :aria-invalid="!!errors.length"
                  :aria-describedby="errors.length ? id + '-phoneNumber-error' : undefined"
                /><small>Include the country code, for example +46.</small>
                <p v-if="errors.length" :id="id + '-phoneNumber-error'" class="invalid-feedback d-block">
                  {{ errors[0] }}
                </p>
              </div>
            </Field>
          </div>
        </section>
        <div class="create-addresses">
          <section class="create-card">
            <h2>Invoice address</h2>
            <p>Used for billing.</p>
            <div class="create-grid">
              <Field v-slot="{ field, errors }" name="clientAddress.countryCode" label="Country" :rules="'required'">
                <div class="create-field">
                  <label :for="id + '-clientAddress-countryCode'">Country *</label><select
                    v-bind="field"
                    :id="id + '-clientAddress-countryCode'"
                    v-model="customer.clientAddress.countryCode"
                    class="form-select"
                    :class="{ 'is-invalid': errors.length }"
                    :aria-invalid="!!errors.length"
                    :aria-describedby="errors.length ? id + '-clientAddress-countryCode-error' : undefined"
                  >
                    <option :value="null" disabled>Select country</option>
                    <option v-for="country in countries" :key="country.value" :value="country.value">
                      {{ country.text }}
                    </option>
                  </select>
                  <p
                    v-if="errors.length"
                    :id="id + '-clientAddress-countryCode-error'"
                    class="invalid-feedback d-block"
                  >
                    {{ errors[0] }}
                  </p>
                </div>
              </Field><Field
                v-slot="{ field, errors }"
                name="clientAddress.addressLine"
                label="Street address"
                :rules="'required'"
              >
                <div class="create-field">
                  <label :for="id + '-clientAddress-addressLine'">Street address *</label><input
                    v-bind="field"
                    :id="id + '-clientAddress-addressLine'"
                    v-model="customer.clientAddress.addressLine"
                    type="text"
                    autocomplete="street-address"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                    :aria-invalid="!!errors.length"
                    :aria-describedby="errors.length ? id + '-clientAddress-addressLine-error' : undefined"
                  />
                  <p
                    v-if="errors.length"
                    :id="id + '-clientAddress-addressLine-error'"
                    class="invalid-feedback d-block"
                  >
                    {{ errors[0] }}
                  </p>
                </div>
              </Field><Field
                v-slot="{ field, errors }"
                name="clientAddress.postalCode"
                label="Postcode"
                :rules="{ required: true, zip: customer.clientAddress.countryCode }"
              >
                <div class="create-field">
                  <label :for="id + '-clientAddress-postalCode'">Postcode *</label><input
                    v-bind="field"
                    :id="id + '-clientAddress-postalCode'"
                    v-model="customer.clientAddress.postalCode"
                    type="text"
                    autocomplete="postal-code"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                    :aria-invalid="!!errors.length"
                    :aria-describedby="errors.length ? id + '-clientAddress-postalCode-error' : undefined"
                  />
                  <p v-if="errors.length" :id="id + '-clientAddress-postalCode-error'" class="invalid-feedback d-block">
                    {{ errors[0] }}
                  </p>
                </div>
              </Field><Field v-slot="{ field, errors }" name="clientAddress.city" label="City" :rules="'required'">
                <div class="create-field">
                  <label :for="id + '-clientAddress-city'">City *</label><input
                    v-bind="field"
                    :id="id + '-clientAddress-city'"
                    v-model="customer.clientAddress.city"
                    type="text"
                    autocomplete="address-level2"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                    :aria-invalid="!!errors.length"
                    :aria-describedby="errors.length ? id + '-clientAddress-city-error' : undefined"
                  />
                  <p v-if="errors.length" :id="id + '-clientAddress-city-error'" class="invalid-feedback d-block">
                    {{ errors[0] }}
                  </p>
                </div>
              </Field><Field v-slot="{ field, errors }" name="clientAddress.addressLine2" label="Address line 2">
                <div class="create-field">
                  <label :for="id + '-clientAddress-addressLine2'">Address line 2 (optional)</label><input
                    v-bind="field"
                    :id="id + '-clientAddress-addressLine2'"
                    v-model="customer.clientAddress.addressLine2"
                    type="text"
                    autocomplete="off"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                    :aria-invalid="!!errors.length"
                    :aria-describedby="errors.length ? id + '-clientAddress-addressLine2-error' : undefined"
                  /><small>Apartment, floor or other address details.</small>
                  <p
                    v-if="errors.length"
                    :id="id + '-clientAddress-addressLine2-error'"
                    class="invalid-feedback d-block"
                  >
                    {{ errors[0] }}
                  </p>
                </div>
              </Field>
            </div>
          </section>
          <section class="create-card">
            <h2>Sender address</h2>
            <p>The default address for outgoing shipments.</p>
            <label class="create-copy"><input v-model="sameAddress" type="checkbox" />Use the invoice address</label>
            <p v-if="sameAddress" class="create-address-help">
              The invoice address will also be saved as the sender address.
            </p>
            <div v-else>
              <div class="create-grid">
                <Field v-slot="{ field, errors }" name="senderAddress.countryCode" label="Country" :rules="'required'">
                  <div class="create-field">
                    <label :for="id + '-senderAddress-countryCode'">Country *</label><select
                      v-bind="field"
                      :id="id + '-senderAddress-countryCode'"
                      v-model="customer.senderAddress.countryCode"
                      class="form-select"
                      :class="{ 'is-invalid': errors.length }"
                      :aria-invalid="!!errors.length"
                      :aria-describedby="errors.length ? id + '-senderAddress-countryCode-error' : undefined"
                    >
                      <option :value="null" disabled>Select country</option>
                      <option v-for="country in countries" :key="country.value" :value="country.value">
                        {{ country.text }}
                      </option>
                    </select>
                    <p
                      v-if="errors.length"
                      :id="id + '-senderAddress-countryCode-error'"
                      class="invalid-feedback d-block"
                    >
                      {{ errors[0] }}
                    </p>
                  </div>
                </Field><Field
                  v-slot="{ field, errors }"
                  name="senderAddress.addressLine"
                  label="Street address"
                  :rules="'required'"
                >
                  <div class="create-field">
                    <label :for="id + '-senderAddress-addressLine'">Street address *</label><input
                      v-bind="field"
                      :id="id + '-senderAddress-addressLine'"
                      v-model="customer.senderAddress.addressLine"
                      type="text"
                      autocomplete="street-address"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      :aria-invalid="!!errors.length"
                      :aria-describedby="errors.length ? id + '-senderAddress-addressLine-error' : undefined"
                    />
                    <p
                      v-if="errors.length"
                      :id="id + '-senderAddress-addressLine-error'"
                      class="invalid-feedback d-block"
                    >
                      {{ errors[0] }}
                    </p>
                  </div>
                </Field><Field
                  v-slot="{ field, errors }"
                  name="senderAddress.postalCode"
                  label="Postcode"
                  :rules="{ required: true, zip: customer.senderAddress.countryCode }"
                >
                  <div class="create-field">
                    <label :for="id + '-senderAddress-postalCode'">Postcode *</label><input
                      v-bind="field"
                      :id="id + '-senderAddress-postalCode'"
                      v-model="customer.senderAddress.postalCode"
                      type="text"
                      autocomplete="postal-code"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      :aria-invalid="!!errors.length"
                      :aria-describedby="errors.length ? id + '-senderAddress-postalCode-error' : undefined"
                    />
                    <p
                      v-if="errors.length"
                      :id="id + '-senderAddress-postalCode-error'"
                      class="invalid-feedback d-block"
                    >
                      {{ errors[0] }}
                    </p>
                  </div>
                </Field><Field v-slot="{ field, errors }" name="senderAddress.city" label="City" :rules="'required'">
                  <div class="create-field">
                    <label :for="id + '-senderAddress-city'">City *</label><input
                      v-bind="field"
                      :id="id + '-senderAddress-city'"
                      v-model="customer.senderAddress.city"
                      type="text"
                      autocomplete="address-level2"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      :aria-invalid="!!errors.length"
                      :aria-describedby="errors.length ? id + '-senderAddress-city-error' : undefined"
                    />
                    <p v-if="errors.length" :id="id + '-senderAddress-city-error'" class="invalid-feedback d-block">
                      {{ errors[0] }}
                    </p>
                  </div>
                </Field><Field v-slot="{ field, errors }" name="senderAddress.addressLine2" label="Address line 2">
                  <div class="create-field">
                    <label :for="id + '-senderAddress-addressLine2'">Address line 2 (optional)</label><input
                      v-bind="field"
                      :id="id + '-senderAddress-addressLine2'"
                      v-model="customer.senderAddress.addressLine2"
                      type="text"
                      autocomplete="off"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      :aria-invalid="!!errors.length"
                      :aria-describedby="errors.length ? id + '-senderAddress-addressLine2-error' : undefined"
                    /><small>Apartment, floor or other address details.</small>
                    <p
                      v-if="errors.length"
                      :id="id + '-senderAddress-addressLine2-error'"
                      class="invalid-feedback d-block"
                    >
                      {{ errors[0] }}
                    </p>
                  </div>
                </Field>
              </div>
            </div>
          </section>
        </div>
        <details class="create-card create-options">
          <summary>Additional settings <span>Optional · can be changed later</span></summary>
          <div class="create-grid">
            <Field v-slot="{ field, errors }" name="shortName" label="Short name" :rules="'alpha|min:3|max:4'">
              <div class="create-field">
                <label :for="id + '-shortName'">Short name (optional)</label><input
                  v-bind="field"
                  :id="id + '-shortName'"
                  v-model="customer.shortName"
                  type="text"
                  autocomplete="off"
                  class="form-control"
                  :class="{ 'is-invalid': errors.length }"
                  :aria-invalid="!!errors.length"
                  :aria-describedby="errors.length ? id + '-shortName-error' : undefined"
                /><small>3–4 letters.</small>
                <p v-if="errors.length" :id="id + '-shortName-error'" class="invalid-feedback d-block">
                  {{ errors[0] }}
                </p>
              </div>
            </Field><Field v-slot="{ field }" name="contractParty">
              <div class="create-field">
                <label :for="id + '-contractParty'">Contract party</label><select
                  v-bind="field"
                  :id="id + '-contractParty'"
                  v-model="customer.contractParty"
                  class="form-select"
                >
                  <option value="">Not assigned</option>
                  <option v-for="option in contracts" :key="String(option.value)" :value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>
            </Field><Field v-slot="{ field }" name="assignedSalesman">
              <div class="create-field">
                <label :for="id + '-assignedSalesman'">Assigned salesperson</label><select
                  v-bind="field"
                  :id="id + '-assignedSalesman'"
                  v-model="customer.assignedSalesman"
                  class="form-select"
                >
                  <option value="">Not assigned</option>
                  <option v-for="option in staffsOptions" :key="String(option.value)" :value="option.value">
                    {{ option.text }}
                  </option>
                </select>
              </div>
            </Field>
          </div>
          <div class="create-toggles">
            <label class="create-toggle"><span>Shipment notifications</span><input v-model="customer.shipmentNotifications" type="checkbox" role="switch" /></label><label class="create-toggle"><span>Inventory management</span><input v-model="customer.isInventoryManagementAvailable" type="checkbox" role="switch" /></label><label class="create-toggle"><span>Address book</span><input v-model="customer.isAddressBookAvailable" type="checkbox" role="switch" /></label><label class="create-toggle"><span>Hide optimized prices</span><input v-model="customer.hidePrice" type="checkbox" role="switch" /></label>
          </div>
        </details>
      </fieldset>
      <div class="create-actions">
        <span>{{
          createdCompanyId
            ? 'Customer saved. Complete the contact’s user account below.'
            : 'Add the customer and its first user.'
        }}</span><b-button
          v-if="!createdCompanyId"
          type="button"
          variant="outline-secondary"
          :disabled="saving"
          @click="cancel"
        >
          Cancel
        </b-button><b-button type="submit" variant="primary" :disabled="saving">
          {{ saving ? 'Adding customer…' : createdCompanyId ? 'Retry user setup' : 'Add customer' }}
        </b-button>
      </div>
    </VeeForm>
  </div>
</template>
<script lang="ts">
import type { CustomerStaffOption, CustomerFormActions, CustomerFormInvalidContext } from '@/types/Components/Customer'
import type { AxiosErrors } from '@/types/Components/Response'
import type { CreateCustomer } from '@/types/Models/Customer'
import type { RouteLocationRaw } from 'vue-router'
import { notify } from '@kyvg/vue3-notification'
import { Field, Form as VeeForm } from 'vee-validate'
import {
  ref,
  computed,
  useId,
  nextTick,
  onMounted,
  defineComponent,
  type Ref,
  type ComputedRef,
  type PropType,
} from 'vue'
import { useRouter } from 'vue-router'
import { none } from '@/services/Helpers'
import { useAccountStore } from '@/store/account'
import { useCustomersStore } from '@/store/customers'
import { usePackingSlipStore } from '@/store/packingSlip'
import { STAFF_CUSTOMERS_LIST } from '@/types/router'
import countries from '@/assets/files/countries.json'
import AccountProxy from '@/proxies/AccountProxy'

export default defineComponent({
  name: 'CreateCustomerForm',
  components: { Field, VeeForm },
  props: {
    routeAfterSave: { type: [String, Object] as PropType<RouteLocationRaw>, default: undefined },
    created: { type: Function as PropType<() => void>, default: undefined },
  },
  emits: ['created', 'cancel'],
  setup(props, { emit }) {
    const customerStore = useCustomersStore()
    const accountStore = useAccountStore()
    const packingSlipStore = usePackingSlipStore()
    const router = useRouter()
    const id = useId()
    const saving: Ref<boolean> = ref(false)
    const error: Ref<string> = ref('')
    const errorSummary: Ref<HTMLElement | null> = ref<HTMLElement | null>(null)
    const createdCompanyId: Ref<string | null> = ref<string | null>(null)
    const sameAddress: Ref<boolean> = ref(true)
    const emptyAddress = (): CreateCustomer['clientAddress'] => ({
      countryCode: null,
      city: null,
      postalCode: null,
      addressLine: null,
      addressLine2: null,
    })
    const customer: Ref<CreateCustomer> = ref<CreateCustomer>({
      name: '',
      clientNumber: '',
      contactName: '',
      contactLastName: '',
      email: '',
      phoneNumber: '',
      clientAddress: emptyAddress(),
      senderAddress: emptyAddress(),
      shortName: '',
      contractParty: '',
      assignedSalesman: accountStore.userId || '',
      shipmentNotifications: false,
      hidePrice: false,
      isInventoryManagementAvailable: false,
      isAddressBookAvailable: false,
    })
    const staffsOptions: ComputedRef<CustomerStaffOption[]> = computed(() =>
      (packingSlipStore.staffs || []).map((staff) => ({
        value: staff.id,
        text: [staff.firstName, staff.lastName].filter(Boolean).join(' ') || staff.email,
      })),
    )
    const contracts = ['Mailworld', '21 grams', 'Europe Post'].map((text) => ({ value: text, text }))
    onMounted(() => {
      if (!packingSlipStore.staffs?.length) packingSlipStore.fetchStaffs()
    })
    function invalidSubmit(context: CustomerFormInvalidContext): Promise<void> {
      error.value = 'Please check the highlighted fields.'
      return nextTick().then(() => {
        const first = Object.keys(context.errors)[0]
        document.getElementById(id + '-' + first?.replaceAll('.', '-'))?.focus()
      })
    }
    function cancel(): void {
      if (props.created) emit('cancel')
      else router.push({ name: STAFF_CUSTOMERS_LIST })
    }
    function handleSubmit(values: Record<string, unknown>, actions: CustomerFormActions): Promise<void> {
      if (saving.value) return Promise.resolve()
      saving.value = true
      error.value = ''
      const data = {
        ...customer.value,
        ...values,
        shipmentNotifications: customer.value.shipmentNotifications,
        hidePrice: customer.value.hidePrice,
        isInventoryManagementAvailable: customer.value.isInventoryManagementAvailable,
        isAddressBookAvailable: customer.value.isAddressBookAvailable,
      } as CreateCustomer
      if (sameAddress.value) data.senderAddress = { ...data.clientAddress }
      const createCompany = createdCompanyId.value
        ? Promise.resolve()
        : customerStore.create(data).then((response) => {
            createdCompanyId.value = response.data.id
          })
      return createCompany
        .then(() =>
          new AccountProxy({ companyId: createdCompanyId.value }).create({
            email: data.email,
            companyId: createdCompanyId.value,
            type: 'customer',
            firstName: data.contactName,
            lastName: data.contactLastName,
            isScannerOnlyEnabled: false,
          }),
        )
        .then(() => {
          notify({ type: 'success', text: `${data.name} and its first user were added.` })
          props.created?.()
          emit('created')
          if (props.routeAfterSave) return router.push(props.routeAfterSave)
        })
        .then(none)
        .catch((failure: AxiosErrors) => {
          if (failure.errors) actions.setErrors(failure.errors)
          const details = failure.errors ? Object.values(failure.errors).flat().join(' ') : failure.message
          error.value = createdCompanyId.value
            ? `The customer was saved, but the user account could not be completed. Retry user setup. ${details || ''}`
            : `The customer could not be added. ${details || 'Please try again.'}`
          return nextTick().then(() => errorSummary.value?.focus())
        })
        .finally(() => {
          saving.value = false
        })
    }
    return {
      customerStore,
      accountStore,
      packingSlipStore,
      router,
      id,
      saving,
      error,
      errorSummary,
      createdCompanyId,
      sameAddress,
      emptyAddress,
      customer,
      staffsOptions,
      contracts,
      invalidSubmit,
      cancel,
      handleSubmit,
      countries,
    }
  },
})
</script>
